import { useCallback } from "react";
import type { AdvisorMovement, ApprovalStatus, Distributor, InventoryAdjustment, InventoryItem } from "@/types";
import type { StoreCtx, TransferStockInput } from "@/store/appTypes";
import {
  deleteAccount as apiDeleteAccount,
  removeAdvisorMovement as apiRemoveAdvisorMovement,
  saveAdvisorMovement,
  saveDistributor,
  saveInventory,
  saveInventoryAdjustment,
  setApprovalStatus as apiSetApprovalStatus,
} from "@/store/api";

// Red de asesores, inventario físico y cuenta corriente (movimientos).
export const useAdvisorOpsActions = (ctx: StoreCtx) => {
  const { data, setData, run } = ctx;

  // Los distribuidores son perfiles de usuario; se crean al registrarse.
  const addDistributor = useCallback(
    (d: Distributor) => {
      setData((p) => ({ ...p, distributors: [...p.distributors, d] }));
    },
    [setData]
  );

  const updateDistributor = useCallback(
    (d: Distributor) =>
      run(async () => {
        const saved = await saveDistributor(d);
        setData((p) => ({
          ...p,
          distributors: p.distributors.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  const setApprovalStatus = useCallback(
    (id: string, status: ApprovalStatus) =>
      run(async () => {
        const saved = await apiSetApprovalStatus(id, status);
        setData((p) => ({
          ...p,
          distributors: p.distributors.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  // Elimina por completo la cuenta de un asesor (usuario + perfil).
  const deleteDistributor = useCallback(
    (id: string) =>
      run(async () => {
        await apiDeleteAccount(id);
        setData((p) => ({
          ...p,
          distributors: p.distributors.filter((x) => x.id !== id),
        }));
      }),
    [run, setData]
  );

  const setInventory = useCallback(
    (item: InventoryItem) =>
      run(async () => {
        const saved = await saveInventory(item);
        setData((p) => {
          const exists = p.inventory.some((x) => x.productId === saved.productId);
          return {
            ...p,
            inventory: exists
              ? p.inventory.map((x) => (x.productId === saved.productId ? saved : x))
              : [...p.inventory, saved],
          };
        });
      }),
    [run, setData]
  );

  // Ajuste de inventario con constancia: guarda el nuevo stock y deja el registro
  // de por qué se ajustó y quién lo hizo (producto, antes, después, razón, usuario).
  const applyInventoryAdjustment = useCallback(
    (item: InventoryItem, adjustment: InventoryAdjustment) =>
      run(async () => {
        const savedItem = await saveInventory(item);
        const savedAdj = await saveInventoryAdjustment(adjustment);
        setData((p) => ({
          ...p,
          inventory: p.inventory.some((x) => x.productId === savedItem.productId)
            ? p.inventory.map((x) => (x.productId === savedItem.productId ? savedItem : x))
            : [...p.inventory, savedItem],
          inventoryAdjustments: [savedAdj, ...p.inventoryAdjustments],
        }));
      }),
    [run, setData]
  );

  // Traspaso de unidades entre dos productos del stock de Oficina. NO registra
  // compra, venta ni movimiento contable: solo mueve el stock y deja constancia
  // (motivo + usuario) en el historial de ajustes. Lanza error si la cantidad es
  // inválida o si no hay unidades suficientes en Oficina.
  const transferInventoryStock = useCallback(
    async (input: TransferStockInput) => {
      const qty = Math.floor(input.qty);
      if (!input.targetId) throw new Error("Selecciona el producto de destino.");
      if (!Number.isFinite(qty) || qty <= 0) {
        throw new Error("La cantidad a traspasar debe ser mayor a cero.");
      }
      if (input.reason.trim().length < 3) {
        throw new Error("Escribe el motivo del traspaso.");
      }
      const source = data.inventory.find((i) => i.productId === input.sourceId);
      const target = data.inventory.find((i) => i.productId === input.targetId);
      const sourceBefore = source?.stockCasa ?? 0;
      const targetBefore = target?.stockCasa ?? 0;
      if (qty > sourceBefore) {
        throw new Error(
          `No hay suficientes unidades en Oficina para traspasar (disponibles: ${sourceBefore}).`
        );
      }

      const sourceItem: InventoryItem = {
        productId: input.sourceId,
        stockCasa: sourceBefore - qty,
        stockPuntoClear: source?.stockPuntoClear ?? 0,
        stockByAdvisor: source?.stockByAdvisor ?? {},
        stockOwnedByAdvisor: source?.stockOwnedByAdvisor ?? {},
      };
      const targetItem: InventoryItem = {
        productId: input.targetId,
        stockCasa: targetBefore + qty,
        stockPuntoClear: target?.stockPuntoClear ?? 0,
        stockByAdvisor: target?.stockByAdvisor ?? {},
        stockOwnedByAdvisor: target?.stockOwnedByAdvisor ?? {},
      };

      const savedSource = await saveInventory(sourceItem);
      const savedTarget = await saveInventory(targetItem);

      const date = new Date().toISOString();
      const adjSource = await saveInventoryAdjustment({
        id: "",
        date,
        productId: input.sourceId,
        productName: input.sourceName,
        qtyBefore: sourceBefore,
        qtyAfter: sourceBefore - qty,
        delta: -qty,
        reason: `Traspaso a ${input.targetName}: ${input.reason.trim()}`,
        userName: input.userName,
        userId: input.userId,
      });
      const adjTarget = await saveInventoryAdjustment({
        id: "",
        date,
        productId: input.targetId,
        productName: input.targetName,
        qtyBefore: targetBefore,
        qtyAfter: targetBefore + qty,
        delta: qty,
        reason: `Traspaso desde ${input.sourceName}: ${input.reason.trim()}`,
        userName: input.userName,
        userId: input.userId,
      });

      setData((p) => {
        const map = new Map(p.inventory.map((x) => [x.productId, x]));
        map.set(savedSource.productId, savedSource);
        map.set(savedTarget.productId, savedTarget);
        return {
          ...p,
          inventory: Array.from(map.values()),
          inventoryAdjustments: [adjTarget, adjSource, ...p.inventoryAdjustments],
        };
      });
    },
    [data, setData]
  );

  const addAdvisorMovement = useCallback(
    (m: AdvisorMovement) =>
      run(async () => {
        const saved = await saveAdvisorMovement(m);
        setData((p) => ({
          ...p,
          advisorMovements: [...p.advisorMovements, saved],
        }));
      }),
    [run, setData]
  );

  const deleteAdvisorMovement = useCallback(
    (id: string) =>
      run(async () => {
        await apiRemoveAdvisorMovement(id);
        setData((p) => ({
          ...p,
          advisorMovements: p.advisorMovements.filter((x) => x.id !== id),
        }));
      }),
    [run, setData]
  );

  return {
    addDistributor,
    updateDistributor,
    setApprovalStatus,
    deleteDistributor,
    setInventory,
    applyInventoryAdjustment,
    transferInventoryStock,
    addAdvisorMovement,
    deleteAdvisorMovement,
  };
};